import { useState, useRef, useEffect, useCallback } from 'react'
import { ListImperativeAPI } from 'react-window'
import { PlatformUserDomainRecord } from '../../../../models/domain/platform-user-domain-record'
import { ListItemType } from '../types'

/**
 * useKeyboardNav — encapsulates keyboard navigation for the domain selector.
 *
 * Manages the focusedIndex state, attaches a document-level keydown listener
 * (when visible), implements findNextInteractive skip logic (skips
 * recents-header and empty items), handles ArrowUp/ArrowDown/Enter/Escape,
 * and calls scrollToRow to keep the focused item visible.
 *
 * Uses a ref-based pattern so the document keydown listener always calls
 * the latest handler without needing to re-register on every render.
 *
 * @param listItems - Current flat list of items being rendered
 * @param listRef - Ref to the react-window VirtualList imperative API
 * @param visible - Whether the popover/list is currently visible
 * @param callbacks - Selection and close callbacks
 * @returns focusedIndex and setFocusedIndex
 *
 * Requirements: 2.9, 2.10, 7.4
 */
export function useKeyboardNav(
    listItems: ListItemType[],
    listRef: React.RefObject<ListImperativeAPI | null>,
    visible: boolean,
    callbacks: {
        onSelectDomain: (domain: PlatformUserDomainRecord) => void
        onSelectOrg: (accountId: number, accountName: string, canAccess: boolean) => void
        onTogglePin: (domainId: number) => void
        onClose: () => void
    },
): {
    focusedIndex: number
    setFocusedIndex: (index: number) => void
} {
    const [focusedIndex, setFocusedIndex] = useState(-1)
    const handleKeyDownRef = useRef<(e: KeyboardEvent) => void>(() => {})

    // ========================================================================
    // Keyboard handler
    // ========================================================================

    /**
     * Handle keyboard navigation
     *
     * Implements keyboard navigation for the domain selector:
     * - ArrowDown: Move focus to next interactive item
     * - ArrowUp: Move focus to previous interactive item
     * - Enter: Select focused item
     * - Escape: Close popover
     *
     * Skips non-interactive items (recents-header, empty) when navigating.
     * Scrolls the virtualized list to reveal the focused item.
     *
     * Requirements: 10.3, 10.4, 10.5, 10.6, 10.11, 16.9
     */
    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            // Only handle keyboard events when popover is visible
            if (!visible) {
                return
            }

            // Helper: find next interactive index in a direction, skipping non-interactive items
            const findNextInteractive = (from: number, direction: 1 | -1): number => {
                let idx = from + direction
                while (idx >= 0 && idx < listItems.length) {
                    const item = listItems[idx]
                    if (item.type === 'domain' || item.type === 'org-header') {
                        return idx
                    }
                    idx += direction
                }
                return from // stay put if nothing found
            }

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault()

                    const nextIndex = findNextInteractive(focusedIndex, 1)
                    setFocusedIndex(nextIndex)

                    if (listRef.current && nextIndex >= 0) {
                        listRef.current.scrollToRow({
                            index: nextIndex,
                            align: 'auto',
                        })
                    }
                    break

                case 'ArrowUp':
                    e.preventDefault()

                    const prevIndex = findNextInteractive(focusedIndex, -1)
                    setFocusedIndex(prevIndex)

                    if (listRef.current && prevIndex >= 0) {
                        listRef.current.scrollToRow({
                            index: prevIndex,
                            align: 'auto',
                        })
                    }
                    break

                case 'Enter':
                    e.preventDefault()

                    // Select focused item if it's a domain or org-header
                    if (focusedIndex >= 0 && focusedIndex < listItems.length) {
                        const item = listItems[focusedIndex]
                        if (item.type === 'domain') {
                            callbacks.onSelectDomain(item.domain)
                        } else if (item.type === 'org-header') {
                            callbacks.onSelectOrg(item.accountId, item.accountName, item.canAccess)
                        }
                    }
                    break

                case 'Escape':
                    e.preventDefault()

                    // Close popover
                    callbacks.onClose()
                    break

                case 'p':
                case 'P':
                    // Toggle pin on focused domain item (skip if typing in search input)
                    if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') {
                        break
                    }
                    if (focusedIndex >= 0 && focusedIndex < listItems.length) {
                        const item = listItems[focusedIndex]
                        if (item.type === 'domain') {
                            e.preventDefault()
                            callbacks.onTogglePin(item.domain.id)
                        }
                    }
                    break
            }
        },
        [visible, listItems, focusedIndex, listRef, callbacks],
    )

    // Keep ref in sync so the document keydown listener always calls the latest handler
    handleKeyDownRef.current = handleKeyDown

    // ========================================================================
    // Document keydown listener
    // ========================================================================

    useEffect(() => {
        // Add keyboard event listener that delegates to ref (always calls latest handler)
        const keydownListener = (e: KeyboardEvent) => handleKeyDownRef.current(e)
        document.addEventListener('keydown', keydownListener)

        return () => {
            document.removeEventListener('keydown', keydownListener)
        }
    }, [])

    return {
        focusedIndex,
        setFocusedIndex,
    }
}
